var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { isNil } from 'lodash-es';
import { freezePosition } from '../../utilities/dom';
import { findTree } from '../node/utilities';
/**
 * A base class for implementing cells in the SDS Tree.
 *
 * @abstract
 */
let SdsTreeCellBase = (() => {
    var _a, _SdsTreeCellBase_colPosition_accessor_storage, _SdsTreeCellBase_rowPosition_accessor_storage;
    let _classSuper = LitElement;
    let _colPosition_decorators;
    let _colPosition_initializers = [];
    let _colPosition_extraInitializers = [];
    let _rowPosition_decorators;
    let _rowPosition_initializers = [];
    let _rowPosition_extraInitializers = [];
    return _a = class SdsTreeCellBase extends _classSuper {
            /**
             * Represents the position of a column in the format `start/end`, where `start` is inclusive and `end` is exclusive.
             *
             * *Note: Columns start from 1 and not 0.*
             *
             * @type {string | undefined}
             *
             * @example
             * // Example usage:
             * // The following represents a column starting from the second cell and ending at the fifth cell.
             * ```html
             * <sds-tree>
             *    <sds-tree-node>
             *       <sds-tree-cell></sds-tree-cell>
             *       <sds-tree-cell col-position="2/5"></sds-tree-cell>
             *    </sds-tree-node>
             * </sds-tree>
             * ```
             */
            get colPosition() { return __classPrivateFieldGet(this, _SdsTreeCellBase_colPosition_accessor_storage, "f"); }
            set colPosition(value) { __classPrivateFieldSet(this, _SdsTreeCellBase_colPosition_accessor_storage, value, "f"); }
            /**
             * Represents the position of a row in the format `start/end`, where `start` is inclusive and `end` is exclusive.
             *
             * *Note: Columns start from 1 and not 0.*
             *
             * @type {string | undefined}
             *
             * @example
             * // Example usage:
             * // The following represents a row starting from the first row and ending at the second row.
             * ```html
             * <sds-tree>
             *    <sds-tree-node>
             *       <sds-tree-cell></sds-tree-cell>
             *       <sds-tree-cell row-position="1/2"></sds-tree-cell>
             *    </sds-tree-node>
             * </sds-tree>
             * ```
             */
            get rowPosition() { return __classPrivateFieldGet(this, _SdsTreeCellBase_rowPosition_accessor_storage, "f"); }
            set rowPosition(value) { __classPrivateFieldSet(this, _SdsTreeCellBase_rowPosition_accessor_storage, value, "f"); }
            disableSelection() {
                this.style.userSelect = 'none';
            }
            enableSelection() {
                this.style.userSelect = 'auto';
            }
            colEnd() {
                return this._colEnd;
            }
            colStart() {
                return this._colStart;
            }
            rowStart() {
                return this._rowStart;
            }
            rowEnd() {
                return this._rowEnd;
            }
            get tree() {
                return this._tree;
            }
            renderInheritedStyles() {
                return undefined;
            }
            setGridRow() {
                const rowStart = this.rowStart();
                const rowEnd = this.rowEnd();
                if (rowStart !== undefined && rowEnd !== undefined) {
                    this.style.gridRowEnd = `span ${rowEnd - rowStart}`;
                }
            }
            setGridColumn() {
                const colStart = this.colStart();
                const colEnd = this.colEnd();
                if (colStart !== undefined && colEnd !== undefined) {
                    this.style.gridColumn = `${colStart + 3}/${colEnd + 3}`;
                }
            }
            connectedCallback() {
                super.connectedCallback();
                this._tree = findTree(this);
            }
            disconnectedCallback() {
                super.disconnectedCallback();
                delete this._tree;
            }
            freeze() {
                var _b;
                if (!isNil(this.tree)) {
                    freezePosition(this, this.tree, (_b = this.tree.shadowRoot) === null || _b === void 0 ? void 0 : _b.getElementById('grid-container'));
                }
            }
            firstUpdated() {
                this.distributeRowPositions();
                this.setGridRow();
                this.distributeColPositions();
                this.setGridColumn();
            }
            distributeColPositions() {
                if (!isNil(this.colPosition)) {
                    const [start, end] = this.colPosition.split('/').map(Number);
                    this._colStart = start;
                    this._colEnd = end;
                }
            }
            distributeRowPositions() {
                if (!isNil(this.rowPosition)) {
                    const [start, end] = this.rowPosition.split('/').map(Number);
                    this._rowStart = start;
                    this._rowEnd = end;
                }
            }
            willUpdate(changedProperties) {
                if (changedProperties.has('rowPosition')) {
                    this.distributeRowPositions();
                    this.setGridRow();
                }
                if (changedProperties.has('colPosition')) {
                    this.distributeColPositions();
                    this.setGridColumn();
                }
            }
            constructor() {
                super(...arguments);
                this._colStart = undefined;
                this._colEnd = undefined;
                this._rowStart = undefined;
                this._rowEnd = undefined;
                this._tree = undefined;
                _SdsTreeCellBase_colPosition_accessor_storage.set(this, __runInitializers(this, _colPosition_initializers, undefined));
                _SdsTreeCellBase_rowPosition_accessor_storage.set(this, (__runInitializers(this, _colPosition_extraInitializers), __runInitializers(this, _rowPosition_initializers, undefined)));
                __runInitializers(this, _rowPosition_extraInitializers);
            }
        },
        _SdsTreeCellBase_colPosition_accessor_storage = new WeakMap(),
        _SdsTreeCellBase_rowPosition_accessor_storage = new WeakMap(),
        (() => {
            var _b;
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create((_b = _classSuper[Symbol.metadata]) !== null && _b !== void 0 ? _b : null) : void 0;
            _colPosition_decorators = [property({ type: String, attribute: 'col-position' })];
            _rowPosition_decorators = [property({ type: String, attribute: 'row-position' })];
            __esDecorate(_a, null, _colPosition_decorators, { kind: "accessor", name: "colPosition", static: false, private: false, access: { has: obj => "colPosition" in obj, get: obj => obj.colPosition, set: (obj, value) => { obj.colPosition = value; } }, metadata: _metadata }, _colPosition_initializers, _colPosition_extraInitializers);
            __esDecorate(_a, null, _rowPosition_decorators, { kind: "accessor", name: "rowPosition", static: false, private: false, access: { has: obj => "rowPosition" in obj, get: obj => obj.rowPosition, set: (obj, value) => { obj.rowPosition = value; } }, metadata: _metadata }, _rowPosition_initializers, _rowPosition_extraInitializers);
            if (_metadata) Object.defineProperty(_a, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        })(),
        _a;
})();
export { SdsTreeCellBase };
